import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface UserManagementModalProps {
  leagueId: number;
  isOpen: boolean;
  onClose: () => void;
  onUserRemoved: () => void;
}

interface Participant {
  id: number;
  user_id: number;
  display_name: string;
  role: string;
  joined_at: string;
  is_manager: boolean;
  total_predictions?: number;
  correct_predictions?: number;
  total_points?: number;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({
  leagueId,
  isOpen,
  onClose,
  onUserRemoved
}) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingUser, setRemovingUser] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadParticipants();
    }
  }, [isOpen, leagueId]);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPickemParticipants(leagueId);
      setParticipants(response.participants);
    } catch (err: any) {
      setError(err.message || 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (participantId: number, displayName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${displayName} from the league? This action cannot be undone.`)) {
      return;
    }

    try {
      setRemovingUser(participantId);
      setError(null);
      setSuccess(null);

      await apiService.removeUserFromLeague(leagueId, participantId);
      
      setSuccess(`Successfully removed ${displayName} from the league`);
      setParticipants(participants.filter(p => p.id !== participantId));
      onUserRemoved();
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setRemovingUser(null);
    }
  };

  const handlePromoteToManager = async (participantId: number, displayName: string) => {
    if (!window.confirm(`Are you sure you want to promote ${displayName} to league manager?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await apiService.promoteUserToManager(leagueId, participantId);
      
      setSuccess(`Successfully promoted ${displayName} to manager`);
      setParticipants(participants.map(p => 
        p.id === participantId ? { ...p, role: 'manager', is_manager: true } : p
      ));
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to promote user');
    }
  };

  const handleDemoteFromManager = async (participantId: number, displayName: string) => {
    if (!window.confirm(`Are you sure you want to demote ${displayName} from manager role?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await apiService.demoteUserFromManager(leagueId, participantId);
      
      setSuccess(`Successfully demoted ${displayName} from manager`);
      setParticipants(participants.map(p => 
        p.id === participantId ? { ...p, role: 'member', is_manager: false } : p
      ));
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to demote user');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredParticipants = participants.filter(participant =>
    participant.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-accent">User Management</h2>
            <button
              onClick={onClose}
              className="text-accent hover:text-white transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800 font-semibold">Total Participants</p>
                  <p className="text-2xl font-bold text-blue-600">{participants.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-800 font-semibold">Managers</p>
                  <p className="text-2xl font-bold text-green-600">
                    {participants.filter(p => p.role === 'manager').length}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-purple-800 font-semibold">Members</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {participants.filter(p => p.role === 'member').length}
                  </p>
                </div>
              </div>

              {/* Participants List */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
                  <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-700">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-2">Role</div>
                    <div className="col-span-2">Joined</div>
                    <div className="col-span-2">Predictions</div>
                    <div className="col-span-2">Points</div>
                    <div className="col-span-1">Actions</div>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {filteredParticipants.map((participant) => (
                    <div key={participant.id} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-3">
                          <p className="font-medium text-primary">{participant.display_name}</p>
                        </div>
                        
                        <div className="col-span-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            participant.role === 'manager'
                              ? 'bg-accent text-primary'
                              : 'bg-gray-200 text-gray-800'
                          }`}>
                            {participant.role === 'manager' ? 'Manager' : 'Member'}
                          </span>
                        </div>
                        
                        <div className="col-span-2 text-sm text-gray-600">
                          {formatDate(participant.joined_at)}
                        </div>
                        
                        <div className="col-span-2 text-sm text-gray-600">
                          {participant.total_predictions || 0} made
                        </div>
                        
                        <div className="col-span-2 text-sm font-medium text-primary">
                          {participant.total_points || 0} pts
                        </div>
                        
                        <div className="col-span-1">
                          <div className="flex space-x-2">
                            {participant.role === 'manager' ? (
                              <button
                                onClick={() => handleDemoteFromManager(participant.id, participant.display_name)}
                                className="text-yellow-600 hover:text-yellow-800 transition-colors"
                                title="Demote from manager"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePromoteToManager(participant.id, participant.display_name)}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Promote to manager"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                </svg>
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleRemoveUser(participant.id, participant.display_name)}
                              disabled={removingUser === participant.id}
                              className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                              title="Remove user"
                            >
                              {removingUser === participant.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {filteredParticipants.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No participants found matching your search.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {filteredParticipants.length} of {participants.length} participants shown
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementModal; 